import * as _ from 'lodash'
import { IBusinessObject } from '../PA.Core/PA.Core.IF.BO';
import { IMessageProvider, IProgressProvider, MessageType } from '../PA.Core/PA.Core.IF.Basic';
import { PAdbContext } from '../PA.Core/PAdbContext';
import { IdentityProvider } from '../PA.Core/Basic/Identity'
import { MessageProvider } from '../PA.Core/Basic/Messages'
import { ProgressProvider } from '../PA.Core/Basic/Progress'
import {
    WB_PPA_ModelRow,
    WB_PPA_ManufacturerRow,
    WB_PPA_MarketRow,
    WB_PPA_ChannelRow,
    WB_PPA_Consumer_SegmentRow,
    WB_PPA_RespondentRow
} from "../PA.Core/PAdbSchemaRows";
import { IwbPPAModel } from "../PA.Core/PAdbSchemaRowsIF";
import {
    esWB_PPA_Model,
    esWB_PPA_Manufacturer,
    esWB_PPA_Respondent,
    esWB_PPA_Market,
    esWB_PPA_Consumer_Segment,
    esWB_PPA_Channel, esWB_PPA_Adjustment
} from "../PA.Core/PAdbSchema";
import { ESRecord } from "../PA.Core/lib/ESRecord";
import { ESDataStore } from "../PA.Core/lib/ESDataStore";
import { CreateOptions, ESRecordResolver } from "../PA.Core/lib/ESRecordResolver";
import { ASync } from "../PA.Core/Basic/ASync";
import { PAEnvironment } from "../PA.Core/PAEnvironment";


export class PPAModelBO implements IBusinessObject {
    Identity: IdentityProvider;

    CreateOptions: any;

    LoadOptions: any;
    _bIsLoaded: boolean;
    _OnLoadComplete: Promise<IBusinessObject>;
    LoadProgressProvider: IProgressProvider;

    ValidateOptions: any;
    _bIsValidated: boolean;
    ValidateMessageProvider: IMessageProvider;
    ValidateProgressProvider: IProgressProvider;

    SaveOptions: any;
    _bIsSaved: boolean;
    SaveProgressProvider: IProgressProvider;

    DeleteOptions: any;
    _bIsDeleted: boolean;
    DeleteMessageProvider: IMessageProvider;
    DeleteProgressProvider: IProgressProvider;

    dbContext: PAdbContext;
    drWB_PPAModel: WB_PPA_ModelRow;

    _bIsIdEditable: boolean;

    constructor(options?: any) {

        this.CreateOptions = options || {};

        if ('dbContext' in this.CreateOptions)
            this.dbContext = this.CreateOptions.dbContext;

        this.Identity = new IdentityProvider({ id: 0, name: 'New Spend Function' });

        this.LoadOptions = {};
        this._bIsLoaded = false;
        this._OnLoadComplete = null;
        this.LoadProgressProvider = new ProgressProvider('Loading', this);

        this.ValidateOptions = {};
        this._bIsValidated = false;
        this.ValidateMessageProvider = new MessageProvider();
        this.ValidateProgressProvider = new ProgressProvider("Validating", this);

        this.SaveOptions = {};
        this._bIsSaved = false;
        this.SaveProgressProvider = new ProgressProvider("Saving", this);

        this.DeleteOptions = {};
        this._bIsDeleted = false;
        this.DeleteMessageProvider = new MessageProvider();
        this.DeleteProgressProvider = new ProgressProvider("Deleting", this);

        this.drWB_PPAModel = null;
        this._bIsIdEditable = true
    }

    get WB_PPAModel(): WB_PPA_ModelRow {
        return this.drWB_PPAModel;
    }

    get bIsIdEditable(): boolean {
        return this._bIsIdEditable
    }

    //<editor-fold desc = "IBusinessObject operations">

    LoadNew(options?: object): Promise<IBusinessObject> {
        const self = this;
        this.LoadProgressProvider.start("Creating PPA Model");
        this._OnLoadComplete = new Promise<IBusinessObject>((resolve, error) => {

            self.LoadOptions = options || {};
            if ('dbContext' in self.LoadOptions)
                self.dbContext = self.LoadOptions.dbContext;
            else
                self.dbContext = new PAdbContext();

            if (this.dbContext == null) {
                const sError = "Unable to determine dbContext for new Model";
                self._OnLoadComplete = null;
                self.LoadProgressProvider.exception(sError);
                error(sError);
                return;
            }

            // Create record using dbContext
            this.dbContext.createRecordWithKey(esWB_PPA_Model)
                .then((newPPAModelRow: WB_PPA_ModelRow) => {
                    newPPAModelRow.setDefaults();
                    self.drWB_PPAModel = newPPAModelRow;
                    self.dbContext.add(esWB_PPA_Model, newPPAModelRow);
                    self.Identity.setAll(self.WB_PPAModel.cif.WB_PPA_MODEL_ID, self.WB_PPAModel.cif.MODEL_ID, self.WB_PPAModel.cif.MODEL_NAME);
                    self.LoadProgressProvider.setName("Loading " + self.Identity.name + "(" + self.Identity.id_ui + ")");
                    self.Identity.listenToRecordUpdates(self.WB_PPAModel, 'WB_PPA_MODEL_ID', 'MODEL_ID', 'MODEL_NAME');
                    self.LoadProgressProvider.finish();
                    self._bIsLoaded = true;
                    self._bIsIdEditable = true;
                    self._OnLoadComplete = null;
                    resolve(self);
                })
                .catch((err) => {
                    self.LoadProgressProvider.exception(err);
                    error(err)
                })
        });

        return this._OnLoadComplete;
    }

    Load(anId: number | number, options?: object): Promise<IBusinessObject> {

        const self = this;

        let recordResolver: ESRecordResolver;
        let recordResolverLoadPromise: Promise<void>;

        self.LoadProgressProvider.start("Loading (" + self.Identity.id_ui + ")");

        self._OnLoadComplete = new Promise<IBusinessObject>((resolve, error) => {

            if (self.bIsLoaded) {
                const sError = "PPA Model already loaded.";
                self._OnLoadComplete = null;
                self.LoadProgressProvider.exception(sError);
                error(sError);
                return;
            }

            self.LoadOptions = options || {};

            if ('dbContext' in self.LoadOptions)
                self.dbContext = self.LoadOptions.dbContext;
            else if (self.dbContext == null)
                self.dbContext = new PAdbContext();

            if (this.dbContext == null) {
                const sError = "Unable to determine dbContext for PPA Model";
                self._OnLoadComplete = null;
                self.LoadProgressProvider.exception(sError);
                error(sError);
                return;
            }

            if ('RecordResolver' in self.LoadOptions) {
                recordResolver = (<any>self.LoadOptions).RecordResolver;
                recordResolverLoadPromise = ASync.resolve()
            } else {
                recordResolver = PPAModelBO.createRecordResolver({ dbContext: self.dbContext });
                recordResolverLoadPromise = PPAModelBO.setupRecordResolver(recordResolver, [anId])
            }

            recordResolverLoadPromise
                .then(function() {
                    const newPPAModelRow: any = recordResolver.get(esWB_PPA_Model, anId);

                    if (_.isEmpty(newPPAModelRow)) {
                        self._OnLoadComplete = null;
                        self.LoadProgressProvider.exception("Id not found.");
                        resolve(self);
                        return;
                    }

                    self.drWB_PPAModel = newPPAModelRow;
                    self.Identity.setAll(self.WB_PPAModel.cif.WB_PPA_MODEL_ID, self.WB_PPAModel.cif.MODEL_ID, self.WB_PPAModel.cif.MODEL_NAME);
                    self.LoadProgressProvider.setName("Loading " + self.Identity.name + "(" + self.Identity.id_ui + ")");
                    self.Identity.listenToRecordUpdates(self.WB_PPAModel, 'WB_PPA_MODEL_ID', 'MODEL_ID', 'MODEL_NAME');
                })
                .then((ignore: any) => {
                    // self._bIsIdEditable = !bExist
                    self._bIsLoaded = true;
                    self._OnLoadComplete = null;
                    self.LoadProgressProvider.finish();
                    resolve(self);
                })
                .catch((err: any) => {
                    self._OnLoadComplete = null;
                    self.LoadProgressProvider.exception(err);
                    error(err);
                });

        });

        return this._OnLoadComplete;
    }

    get OnLoadComplete(): Promise<IBusinessObject> {
        return this._OnLoadComplete;
    }

    get bIsLoaded(): boolean {
        return this._bIsLoaded;
    }

    async Validate(options?: any): Promise<IBusinessObject> {

        const self = this;

        self.ValidateMessageProvider.clearMessages();
        self.ValidateProgressProvider.start("Validating " + self.WB_PPAModel.cif.Model_NAME + "(" + self.WB_PPAModel.cif.Model_ID + ")");

        // Does no validations yet
        self.ValidateOptions = options || {};
        try {
            if (self.dbContext == undefined) {
                const sError = "Unable to determine dbContext to Validate PPA Model Data Source.";
                self.ValidateProgressProvider.exception(sError);
                self.ValidateMessageProvider.addMessage({
                    code: "nodbcontext",
                    type: MessageType.Error,
                    title: sError,
                    description: "",
                    subtitle: "",
                    count: 0
                })
            }

            /*    if (self.drWB_PPAModel.cif.FUNCTION_DEFN_SRC.replace(" ", "") === "") {
                    const sError = "The java expression cannot be empty";
                    self.ValidateProgressProvider.exception(sError);
                    self.ValidateMessageProvider.addMessage({
                        code: "functionempty",
                        type: MessageType.Error,
                        title: sError,
                        description: "",
                        subtitle: "",
                        count: 0
                    })
                }
           */
            // await ModelPredictorBOValidations.ValidateFields(self)

            self._bIsValidated = self.ValidateMessageProvider.countMessages(MessageType.Error) <= 0;
            self.ValidateProgressProvider.finish();
        } catch (err) {
            self._bIsValidated = false;
            self.ValidateProgressProvider.exception(err);
            return self;
        }

        return self
    }

    get bIsValidated(): boolean {
        return this._bIsValidated;
    }

    Save(options?: any): Promise<IBusinessObject> {
        return this.SaveUsing(false, options)
    }

    SaveBatch(options?: any): Promise<IBusinessObject> {
        return this.SaveUsing(true, options)
    }

    async SaveUsing(bBatch: boolean, options?: any): Promise<IBusinessObject> {
        const self = this;

        const wbPPAModel = <IwbPPAModel>self.drWB_PPAModel;
        try {
            self.SaveProgressProvider.start("Saving " + wbPPAModel.MODEL_NAME + "(" + wbPPAModel.MODEL_ID + ")");
            self._bIsSaved = false;
            self.SaveOptions = options || {};

            if (_.isEmpty(self.dbContext)) {
                const sError = "Unable to determine dbContext to Save PPA Model";
                self.SaveProgressProvider.exception(sError);
                return
            }

            let saveFunction: (esName: string, saveRecords: ESRecord[] | ESRecord, options?: any) => Promise<ESDataStore>;
            if (bBatch) {
                saveFunction = self.dbContext.SaveBatch.bind(self.dbContext);
            } else {
                saveFunction = self.dbContext.Save.bind(self.dbContext);
            }


            await self.Validate({})

            if (!self.bIsValidated) {
                self._bIsSaved = false;
                self.SaveProgressProvider.finish();
                return self;
            }

            await saveFunction(esWB_PPA_Model, self.drWB_PPAModel, {})

            const tasks: Promise<any>[] = []

            tasks.push(saveFunction(esWB_PPA_Market, self.drWB_PPAModel.WB_PPAMarketRows))
            tasks.push(saveFunction(esWB_PPA_Consumer_Segment, _.flatMap(self.drWB_PPAModel.WB_PPAMarketRows, mRow=>{
                return mRow.WB_PPAConsumerSegmentRows
                })))

            const  RespondentRows=<any[]>_.flatMap(self.drWB_PPAModel.WB_PPAMarketRows, mRow => {
                _.forEach(mRow.WB_PPAConsumerSegmentRows, sRow=>{
                    return sRow.WB_PPARespondentRows
                })
            })
            tasks.push(saveFunction(esWB_PPA_Respondent, RespondentRows))
            tasks.push(saveFunction(esWB_PPA_Adjustment, self.drWB_PPAModel.WB_PPAAdjustmentRows))

            await Promise.all(tasks)
            if (bBatch) {
                self.dbContext.addBatchCompleteListener(() => {
                    self._bIsSaved = true;
                })
            } else {
                self._bIsSaved = true;
            }

            self._bIsIdEditable = false
            self.SaveProgressProvider.finish();
            return self;
        } catch (err) {
            self.SaveProgressProvider.exception(err);
            return (self)
        }

    }

    get bIsSaved(): boolean {
        return this._bIsSaved;
    }

    //Delete
    Delete(options?: any): Promise<IBusinessObject> {
        return this.DeleteUsing(true, options)
    }

    DeleteOnSave(options?: any): Promise<IBusinessObject> {
        return this.DeleteUsing(false, options)
    }

    private DeleteUsing(bSaveNow: boolean, options?: any): Promise<IBusinessObject> {
        const self = this;
        const wbPPAModel = <IwbPPAModel>self.drWB_PPAModel;
        this.DeleteMessageProvider.clearMessages();
        this.DeleteProgressProvider.start("Deleting " + wbPPAModel.SPEND_FUNCTION_NAME + "(" + wbPPAModel.SPEND_FUNCTION_ID + ")");

        return new Promise<IBusinessObject>((resolve, error) => {

            // 1. Validate if we can delete the Model Predictor

            // 2. If Valid, mark all the records for deletion.
            self.drWB_PPAModel.markDeleted();

            if (bSaveNow) {
                self.SaveUsing(false)
                    .then((ignore) => {
                        self._bIsDeleted = true;
                        self.DeleteProgressProvider.finish();
                        resolve(self);
                    })
                    .catch((err) => {
                        this.DeleteProgressProvider.exception(err);
                        resolve(self)
                    })
            } else {
                self._bIsDeleted = true;
                self.DeleteProgressProvider.finish();
                resolve(self);
            }
        });
    }

    get bIsDeleted(): boolean {
        return this._bIsDeleted;
    }

    addPPAModelMarket(): Promise<WB_PPA_MarketRow> {
        const self = this;
        return new Promise<WB_PPA_MarketRow>((resolve, error) => {
            const found = false

            return self.dbContext.createRecordWithKey(esWB_PPA_Market)
                .then((aNewRow: WB_PPA_MarketRow) => {
                    aNewRow.setDefaults();
                    aNewRow.cif.WB_PPA_Model_ID = self.drWB_PPAModel.cif.WB_PPA_Model_ID;
                    aNewRow.WB_PPAModelRow = self.drWB_PPAModel

                    self.dbContext.add(esWB_PPA_Market, aNewRow);
                    resolve(aNewRow)
                })
                .catch((err) => {
                    error(err)
                })
        })
    }

    removePPAModelMarket(aPrdId: number): Promise<void> {
        const self = this
        return new Promise<void>((resolve, error) => {
            _.forEach(self.drWB_PPAModel.WB_PPAMarketRows, (x: WB_PPA_MarketRow, index: number) => {
                if (x.isActive && x.cif.WB_PPA_PRODUCT_ID == aPrdId) {
                    x.markDeleted()
                }
            })
            resolve()
        })
    }

    static createRecordResolver(boLoadOptions: any): ESRecordResolver {

        const opts = boLoadOptions || {};
        let dbContext: PAdbContext;

        if ('dbContext' in opts) {
            dbContext = opts.dbContext;
        } else {
            dbContext = new PAdbContext({});
        }

        const PPAModel_options: CreateOptions = {
            standardConditions: {},
            dctIncludeBelongsToNamedRelations: {}
        };

        const aRecResolver = new ESRecordResolver(dbContext, PPAModel_options);

        return aRecResolver;
    }

    static setupRecordResolver(recordResolver: ESRecordResolver, ids: string[] | number[]): Promise<void> {

        return new Promise<void>((resolve, error) => {
            recordResolver.findAllWithRelationships(esWB_PPA_Model, ids).then((result) => {
                resolve()
            }).catch((err) => {
                error(err)
            })
        })
    }

    static concurrentLoadCountHint(): number {
        return 10;
    }

    //</editor-fold>
}

PAEnvironment.registerBusinessObjectClass(PPAModelBO, "PPAModelBO", "PPA Model")
